/**
 * Mock routes: simulated responses for network requests (docs/sandbox.md
 * §3.3), defined by the context or an example. Unmatched requests get an
 * empty `200` (or, for sockets and streams, silence).
 */

export interface MockRoute {
  /**
   * The absolute URL to answer. `*` matches anything (including `/`).
   * Without `*` and without a query, the request's query and fragment are
   * ignored.
   */
  url: string;
  /** Only this method (case-insensitive); any method if absent. */
  method?: string;
  /** Default 200. */
  status?: number;
  headers?: Record<string, string>;
  body?: string;
  /** Simulated latency in virtual ms (default: the profile's latency). */
  delayMs?: number;
  /** Fail like a network error (`TypeError: Failed to fetch`, an `error` event). */
  error?: boolean;
  /** For WebSocket and EventSource: messages the server sends after connecting. */
  messages?: string[];
}

const stripTail = (url: string): string => url.replace(/[?#].*$/, "");

function patternMatches(pattern: string, url: string): boolean {
  if (pattern.includes("*")) {
    const source = pattern
      .split("*")
      .map((part) => part.replace(/[.+?^${}()|[\]\\]/g, "\\$&"))
      .join(".*");
    return new RegExp(`^${source}$`).test(url);
  }
  if (pattern.includes("?")) return url.replace(/#.*$/, "") === pattern;
  return stripTail(url) === stripTail(pattern);
}

/** The first route answering `method url`, or null. */
export function matchRoute(
  routes: readonly MockRoute[],
  method: string,
  url: string,
): MockRoute | null {
  const upper = method.toUpperCase();
  for (const route of routes) {
    if (route.method !== undefined && route.method.toUpperCase() !== upper) continue;
    if (patternMatches(route.url, url)) return route;
  }
  return null;
}
